// The wire of the Curator API's read of a hub's repositories: a batch of
// WarmHub reads, each named by its tag, and each read's result, in the order
// asked. A tool's reads (get, getMany, query, count, refs, search, shape) take
// the Tool SDK's own option schemas, so a WarmHubReader built over this route
// takes what tools pass; Repo and Shapes are the hub agent's survey of a
// whole repository. Part of the Curator API contract, so it imports only
// effect and the Tool SDK. See Design > Exchange, "Reading a hub's
// repositories".

import { Schema } from "effect";
import { ShapeDef } from "../tool-sdk/defs.ts";
import { FailureReason } from "../tool-sdk/errors.ts";
import { Json } from "../tool-sdk/json.ts";
import { CountOptions, QueryOptions, RefsOptions, SearchOptions, ThingRecord } from "../tool-sdk/types.ts";

/** At most how many reads one request carries. */
export const MAX_READS = 50;
/** At most how many refs one GetMany names. */
export const MAX_GET_MANY = 100;
/** The furthest offset a Query read seeks to. */
export const MAX_OFFSET = 10_000_000;

// A repository as WarmHub names it, "org/name".
const Repo = Schema.String.check(Schema.isPattern(/^[^/\s]+\/[^/\s]+$/), Schema.isMaxLength(200));
const Ref = Schema.NonEmptyString.check(Schema.isMaxLength(500));

/** One read, in one repository the request names. */
export const WarmHubRead = Schema.TaggedUnion({
  /** The repository's description, visibility, Thing count by shape, and indexed fields. */
  Repo: { repo: Repo },
  /** Every shape in the repository, with its current definition. */
  Shapes: { repo: Repo },
  Get: { repo: Repo, ref: Ref },
  GetMany: { repo: Repo, refs: Schema.Array(Ref).check(Schema.isMaxLength(MAX_GET_MANY)) },
  // With an offset, the page that starts that many Things into the query's
  // order, so a survey can sample away from the head. WarmHub has no
  // documented offset read yet (warmhub/warmhub-app#13751): the Exchange
  // seeks with its query cursor, and the caller checks the page's position.
  Query: { repo: Repo, options: QueryOptions, offset: Schema.optionalKey(Schema.Int.check(Schema.isGreaterThanOrEqualTo(0), Schema.isLessThanOrEqualTo(MAX_OFFSET))) },
  Count: { repo: Repo, options: CountOptions },
  Refs: { repo: Repo, ref: Ref, options: RefsOptions },
  Search: { repo: Repo, text: Schema.NonEmptyString.check(Schema.isMaxLength(500)), options: SearchOptions },
  Shape: { repo: Repo, name: Schema.NonEmptyString, version: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)) },
});
export type WarmHubRead = typeof WarmHubRead.Type;

export const ReadsRequest = Schema.Struct({ reads: Schema.Array(WarmHubRead).check(Schema.isMinLength(1), Schema.isMaxLength(MAX_READS)) });

/**
 * One field declared for indexing in the repository: `ready` when every
 * shape carrying it has its index ready, and each carrying shape's own
 * marker, since a `where` on one shape is served by that shape's marker.
 */
export const IndexedField = Schema.Struct({
  fieldPath: Schema.String,
  ready: Schema.Boolean,
  carrying: Schema.Array(Schema.Struct({ shapeName: Schema.String, state: Schema.String })),
});
export const RepoSummary = Schema.Struct({
  repo: Schema.String,
  description: Schema.String,
  visibility: Schema.Literals(["public", "private"]),
  thingsByShape: Schema.Record(Schema.String, Schema.Int),
  indexedFields: Schema.Array(IndexedField),
});
export type RepoSummary = typeof RepoSummary.Type;
/** A shape and its current version's definition, as WarmHub stores it, or null when it has none. */
export const ShapeSummary = Schema.Struct({
  name: Schema.String,
  active: Schema.Boolean,
  shapeSystem: Schema.Boolean,
  version: Schema.NullOr(Schema.Int),
  definition: Schema.NullOr(Json),
});
export type ShapeSummary = typeof ShapeSummary.Type;
export const RefItem = Schema.Struct({ id: Schema.String, shapeName: Schema.optionalKey(Schema.String), fieldPath: Schema.optionalKey(Schema.String) });

/**
 * A read that failed, beside the others: WarmHub's `code` when it sent one,
 * such as FIELD_NOT_INDEXABLE, and `retryable`, so a caller tells an outage
 * from a refusal without reading `detail`, which is text the Exchange wrote.
 * A repository outside the hub's readable repositories is Refused with code
 * OUTSIDE_READABLE_REPOSITORIES. See Design > Errors, "A batch's failed
 * reads".
 */
export const ReadFailed = Schema.TaggedStruct("ReadFailed", {
  reason: FailureReason,
  retryable: Schema.Boolean,
  code: Schema.optionalKey(Schema.String),
  detail: Schema.String,
  retryAfterSeconds: Schema.optionalKey(Schema.Finite),
});
export type ReadFailed = typeof ReadFailed.Type;

// A Thing and a shape definition as the wire carries them: their versions
// are whole numbers, so a client's OpenAPI document has no non-finite ones.
export const WireThing = Schema.Struct({ ...ThingRecord.fields, version: Schema.Int });
const WireShapeDef = Schema.Struct({ ...ShapeDef.fields, version: Schema.Int });

/** A read's result: the value of the read whose tag it carries, or ReadFailed. */
export const ReadResult = Schema.Union([
  Schema.TaggedStruct("Repo", { summary: RepoSummary }),
  Schema.TaggedStruct("Shapes", { shapes: Schema.Array(ShapeSummary) }),
  Schema.TaggedStruct("Get", { thing: WireThing }),
  Schema.TaggedStruct("GetMany", { things: Schema.Array(WireThing) }),
  Schema.TaggedStruct("Query", { things: Schema.Array(WireThing) }),
  Schema.TaggedStruct("Count", { count: Schema.Int }),
  Schema.TaggedStruct("Refs", { items: Schema.Array(RefItem), nextCursor: Schema.optionalKey(Schema.String) }),
  Schema.TaggedStruct("Search", { things: Schema.Array(WireThing) }),
  Schema.TaggedStruct("Shape", { shape: WireShapeDef }),
  ReadFailed,
]);
export type ReadResult = typeof ReadResult.Type;

export const ReadsResponse = Schema.Struct({ results: Schema.Array(ReadResult) });
